// src/components/PartnerSpotlight.jsx
import React from 'react';
import { Award, MapPin, Phone, Heart } from 'lucide-react';
import voucherImage from '../assets/images/christos-voucher.jpeg';

/**
 * Partner Spotlight — a premium "spotlight" band that honours a key CCM partner.
 * Designed to deliberately contrast the site's light sections: a deep, near-black
 * stage with a sweeping gold spotlight, framing the partner like an award recipient.
 */
const PartnerSpotlight = () => {
    return (
        <section className="relative overflow-hidden bg-[#0b0710] py-24 sm:py-32">
            {/* Stage backdrop: layered radial glows + a slow sweeping spotlight */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(212,175,55,0.18),transparent_55%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_120%,rgba(147,51,234,0.22),transparent_55%)]" />
                <div
                    className="absolute -top-1/3 left-1/2 h-[160%] w-[55%] -translate-x-1/2
                               bg-[conic-gradient(from_180deg_at_50%_0%,transparent_0deg,rgba(226,192,103,0.16)_25deg,transparent_55deg)]
                               blur-2xl motion-safe:animate-spotlight-sweep"
                />
                {/* Fine grain for texture */}
                <div
                    className="absolute inset-0 opacity-[0.04] mix-blend-screen"
                    style={{
                        backgroundImage:
                            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                    }}
                />
            </div>

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {/* Section eyebrow */}
                <div className="mb-14 text-center">
                    <span className="inline-flex items-center gap-2 rounded-full border border-gold-400/30
                                     bg-gold-400/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em]
                                     text-gold-200 backdrop-blur-sm">
                        <Award className="h-4 w-4" />
                        Premier Partner
                    </span>
                    <h2 className="mt-5 font-display text-4xl font-semibold text-white sm:text-5xl">
                        Partner{' '}
                        <span className="bg-gradient-to-r from-gold-200 via-gold-400 to-gold-200 bg-[length:200%_auto]
                                         bg-clip-text italic text-transparent motion-safe:animate-gold-shimmer">
                            Spotlight
                        </span>
                    </h2>
                    <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-purple-100/70">
                        Honouring the partners whose generosity makes our mission possible.
                    </p>
                </div>

                <div className="grid items-center gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
                    {/* Voucher — framed like a presented gift, fully visible (no cropping) */}
                    <div className="group relative mx-auto w-full max-w-lg">
                        <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-gold-400/40 via-gold-200/10 to-transparent opacity-70 blur-2xl transition-opacity duration-700 group-hover:opacity-100" />
                        <div className="relative rotate-[-2deg] rounded-2xl border border-gold-400/30 bg-white/[0.03] p-3 shadow-2xl backdrop-blur-sm transition-transform duration-700 ease-out group-hover:rotate-0">
                            <img
                                src={voucherImage}
                                alt="Christo's Supermarket $50 gift voucher — redeemable at Old Parham Road and High Street"
                                className="w-full rounded-xl object-contain"
                            />
                            <div className="pointer-events-none absolute inset-3 rounded-xl ring-1 ring-inset ring-white/10" />
                        </div>
                        <span className="absolute -right-3 -top-3 z-10 inline-flex rotate-[8deg] items-center rounded-full
                                         bg-gradient-to-br from-gold-300 to-gold-500 px-4 py-1.5 text-sm font-bold
                                         text-[#0b0710] shadow-lg">
                            $50 Gift Voucher
                        </span>
                    </div>

                    {/* Partner narrative */}
                    <div>
                        <h3 className="font-display text-3xl font-semibold leading-tight text-white sm:text-4xl">
                            Christo&rsquo;s Supermarket
                        </h3>
                        <p className="mt-2 font-display text-xl italic text-gold-300">
                            &ldquo;Stretching your dollars&rdquo;
                        </p>

                        <div className="mt-6 h-px w-24 bg-gradient-to-r from-gold-400/80 to-transparent" />

                        <p className="mt-6 text-lg leading-relaxed text-purple-100/80">
                            A cornerstone of our community, Christo&rsquo;s Supermarket has been a
                            steadfast supporter of the Community Charitable Ministry for years. Their
                            unwavering generosity enables us to continue our initiatives that help the
                            needy, our youth, and cancer survivors.
                        </p>
                        <p className="mt-4 text-lg leading-relaxed text-purple-100/80">
                            We are honoured to have them stand with us as we work to empower those in need.
                        </p>

                        {/* Partner details pulled from their voucher */}
                        <div className="mt-8 grid gap-4 sm:grid-cols-2">
                            <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4">
                                <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold-300" />
                                <div>
                                    <p className="text-sm font-semibold text-white">Two Locations</p>
                                    <p className="text-sm text-purple-100/70">
                                        Old Parham Road &amp; High Street
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4">
                                <Phone className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold-300" />
                                <div>
                                    <p className="text-sm font-semibold text-white">Get in Touch</p>
                                    <p className="text-sm text-purple-100/70">462-1268 &middot; 462-1073</p>
                                </div>
                            </div>
                        </div>

                        <p className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-gold-200/90">
                            <Heart className="h-4 w-4 fill-gold-300/40 text-gold-300" />
                            Proudly supporting CCM &mdash; Supermarket &amp; Wholesale
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PartnerSpotlight;
